import { useState, useEffect } from 'react';
import { useTheme } from '../theme';
import { useToast } from '../Toast';

const COMPONENT_DESCRIPTIONS = {
  amountNormalized: 'Large transaction amounts relative to baseline',
  structuringScore: 'Multiple small transactions to avoid reporting thresholds',
  roundAmountScore: 'Suspiciously round amounts (multiples of 1000, 5000)',
  walletRisk: 'Known mixer, darknet, or sanctioned wallet addresses',
  jurisdictionRisk: 'High-risk country transactions (Iran, North Korea, Syria, Cuba)',
  velocityScore: 'Rapid transaction sequences in short time windows',
  outlierScore: 'Deviation from customer\'s historical behavior (Z-score)',
  newEntityScore: 'First transaction with new counterparty or entity',
  firstHopRisk: 'Direct transfer from known illicit sources',
  anomalousTimingScore: 'Unusual transaction timing (off-hours, holidays)',
};

export default function AMLModelConfiguration({ company }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState({});
  const [thresholds, setThresholds] = useState({});
  const [originalWeights, setOriginalWeights] = useState({});
  const [originalThresholds, setOriginalThresholds] = useState({});
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (company?.id) loadConfiguration();
  }, [company?.id]);

  async function loadConfiguration() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('amlguard_token');
      if (!token) {
        console.warn('[AML Config] No auth token available');
        setLoading(false);
        return;
      }

      // Load config
      console.log('[AML Config] Loading configuration for company:', company.id);
      const configRes = await fetch(`/api/aml/config?companyId=${company.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (configRes.ok) {
        const data = await configRes.json();
        console.log('[AML Config] Loaded config:', data.config);
        setWeights(data.config.weights);
        setThresholds(data.config.thresholds);
        setOriginalWeights(JSON.parse(JSON.stringify(data.config.weights)));
        setOriginalThresholds(JSON.parse(JSON.stringify(data.config.thresholds)));
      } else {
        console.error('[AML Config] Load failed:', configRes.status, configRes.statusText);
      }

      // Load stats
      const statsRes = await fetch(`/api/aml/stats?companyId=${company.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        console.log('[AML Config] Loaded stats:', data.stats);
        setStats(data.stats);
      } else {
        console.warn('[AML Stats] Load failed:', statsRes.status);
      }
    } catch (err) {
      console.error('[AML Config] Error loading configuration:', err);
      showToast('Error loading model configuration: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      const token = localStorage.getItem('amlguard_token');
      if (!token) {
        showToast('No authentication token', 'error');
        return;
      }

      const res = await fetch('/api/aml/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          companyId: company.id,
          weights,
          thresholds
        })
      });

      if (!res.ok) {
        const err = await res.json();
        console.error('[AML Config] Save error:', err);
        showToast(err.error || 'Error saving configuration', 'error');
        return;
      }

      const result = await res.json();
      console.log('[AML Config] Configuration saved successfully:', result.config);
      showToast('✅ Model configuration saved successfully', 'success');

      // Update state immediately without reload
      setOriginalWeights(JSON.parse(JSON.stringify(weights)));
      setOriginalThresholds(JSON.parse(JSON.stringify(thresholds)));

      // Reload config to verify
      setTimeout(() => loadConfiguration(), 500);
    } catch (err) {
      console.error('[AML Config] Error saving configuration:', err);
      showToast('Error saving configuration: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleResetWeights() {
    if (!confirm('Reset all weights to defaults?')) return;
    setWeights(JSON.parse(JSON.stringify(originalWeights)));
  }

  function handleResetThresholds() {
    if (!confirm('Reset all thresholds to defaults?')) return;
    setThresholds(JSON.parse(JSON.stringify(originalThresholds)));
  }

  function handleWeightChange(key, value) {
    const numValue = Math.max(0, Math.min(1, parseFloat(value) || 0));
    setWeights(prev => ({ ...prev, [key]: numValue }));
  }

  function handleThresholdChange(key, value) {
    const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    setThresholds(prev => ({ ...prev, [key]: numValue }));
  }

  const weightsSum = Object.values(weights).reduce((a, b) => a + b, 0);
  const isWeightsValid = Math.abs(weightsSum - 1.0) < 0.01;
  const hasChanges = JSON.stringify(weights) !== JSON.stringify(originalWeights) ||
                     JSON.stringify(thresholds) !== JSON.stringify(originalThresholds);

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading configuration...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          📊 AML Quantitative Model Configuration
        </h2>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Customize risk scoring weights and alert thresholds for your organization
        </p>
      </div>

      {/* Statistics Panel */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '1rem' }}>
            <div style={{ color: C.textDim, fontSize: '0.75rem', marginBottom: '0.25rem' }}>Total Transactions</div>
            <div style={{ color: C.text, fontSize: '1.5rem', fontWeight: 700 }}>{stats.totalTransactions.toLocaleString()}</div>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '1rem' }}>
            <div style={{ color: C.textDim, fontSize: '0.75rem', marginBottom: '0.25rem' }}>Alerts Generated</div>
            <div style={{ color: '#FF6B6B', fontSize: '1.5rem', fontWeight: 700 }}>{stats.alertCount}</div>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '1rem' }}>
            <div style={{ color: C.textDim, fontSize: '0.75rem', marginBottom: '0.25rem' }}>Flagged for Review</div>
            <div style={{ color: '#FFA94D', fontSize: '1.5rem', fontWeight: 700 }}>{stats.flaggedCount}</div>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '1rem' }}>
            <div style={{ color: C.textDim, fontSize: '0.75rem', marginBottom: '0.25rem' }}>Model Accuracy</div>
            <div style={{ color: '#51CF66', fontSize: '1.5rem', fontWeight: 700 }}>{stats.modelAccuracy}%</div>
          </div>
        </div>
      )}

      {/* Component Weights */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: C.text, fontFamily: "'Syne', sans-serif", margin: 0 }}>Component Weights</h3>
          <div style={{ fontSize: '0.85rem', color: C.textMid }}>
            Sum: <span style={{ fontWeight: 700, color: isWeightsValid ? '#51CF66' : '#FF6B6B' }}>
              {weightsSum.toFixed(3)}
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1.25rem' }}>
          {Object.entries(weights).map(([key, value]) => (
            <div key={key} style={{
              display: 'grid',
              gridTemplateColumns: '200px 80px 1fr',
              gap: '1rem',
              alignItems: 'center',
              paddingBottom: '1rem',
              borderBottom: `1px solid ${C.border}`
            }}>
              <div>
                <div style={{ color: C.text, fontWeight: 600, fontSize: '0.9rem' }}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
                <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {COMPONENT_DESCRIPTIONS[key]}
                </div>
              </div>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={value.toFixed(2)}
                onChange={(e) => handleWeightChange(key, e.target.value)}
                style={{
                  padding: '0.5rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem',
                  textAlign: 'center'
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  height: '8px',
                  background: C.accent,
                  borderRadius: 4,
                  flex: value,
                  maxWidth: '100%'
                }}></div>
                <div style={{ color: C.text, fontSize: '0.85rem', fontWeight: 600, minWidth: '45px' }}>
                  {(value * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          ))}
        </div>

        {!isWeightsValid && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#FF6B6B22',
            border: '1px solid #FF6B6B',
            borderRadius: 6,
            color: '#FF6B6B',
            fontSize: '0.85rem'
          }}>
            ⚠️ Weights must sum to 1.0 (currently {weightsSum.toFixed(3)}). Adjust weights to proceed.
          </div>
        )}
      </div>

      {/* Alert Thresholds */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ color: C.text, fontFamily: "'Syne', sans-serif", marginBottom: '1.5rem' }}>
          Alert Thresholds
        </h3>

        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                🚨 Alert Threshold
              </label>
              <p style={{ color: C.textDim, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                Risk score ≥ this value generates immediate alert
              </p>
              <input
                type="number"
                min="0"
                max="100"
                value={thresholds.alertThreshold || 70}
                onChange={(e) => handleThresholdChange('alertThreshold', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '1rem',
                  fontWeight: 700
                }}
              />
              <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.5rem' }}>
                Current: {thresholds.alertThreshold || 70}
              </div>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                🟠 Flag Threshold
              </label>
              <p style={{ color: C.textDim, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                Risk score ≥ this value flags for manual review
              </p>
              <input
                type="number"
                min="0"
                max="100"
                value={thresholds.flagThreshold || 50}
                onChange={(e) => handleThresholdChange('flagThreshold', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '1rem',
                  fontWeight: 700
                }}
              />
              <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.5rem' }}>
                Current: {thresholds.flagThreshold || 50}
              </div>
            </div>
          </div>

          {thresholds.alertThreshold <= thresholds.flagThreshold && (
            <div style={{
              padding: '0.75rem',
              background: '#FF6B6B22',
              border: '1px solid #FF6B6B',
              borderRadius: 6,
              color: '#FF6B6B',
              fontSize: '0.85rem'
            }}>
              ⚠️ Alert threshold must be greater than flag threshold
            </div>
          )}
        </div>
      </div>

      {/* Threshold Visualization */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ color: C.text, fontFamily: "'Syne', sans-serif", marginBottom: '1rem' }}>
          Risk Score Scale
        </h3>
        <div style={{ display: 'flex', height: '60px', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{
            flex: thresholds.flagThreshold || 50,
            background: '#51CF6622',
            border: `1px solid #51CF66`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#51CF66',
            fontSize: '0.75rem',
            fontWeight: 600
          }}>
            Clean {thresholds.flagThreshold}
          </div>
          <div style={{
            flex: (thresholds.alertThreshold || 70) - (thresholds.flagThreshold || 50),
            background: '#FFA94D22',
            border: `1px solid #FFA94D`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFA94D',
            fontSize: '0.75rem',
            fontWeight: 600
          }}>
            Flagged {(thresholds.alertThreshold || 70) - (thresholds.flagThreshold || 50)}
          </div>
          <div style={{
            flex: 100 - (thresholds.alertThreshold || 70),
            background: '#FF6B6B22',
            border: `1px solid #FF6B6B`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FF6B6B',
            fontSize: '0.75rem',
            fontWeight: 600
          }}>
            Alert {100 - (thresholds.alertThreshold || 70)}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={handleSave}
          disabled={!hasChanges || !isWeightsValid || thresholds.alertThreshold <= thresholds.flagThreshold}
          style={{
            padding: '0.75rem 1.5rem',
            background: hasChanges && isWeightsValid && thresholds.alertThreshold > thresholds.flagThreshold ? C.accent : C.border,
            color: hasChanges && isWeightsValid && thresholds.alertThreshold > thresholds.flagThreshold ? '#0A0E1A' : C.textDim,
            border: 'none',
            borderRadius: 6,
            fontWeight: 700,
            cursor: hasChanges && isWeightsValid && thresholds.alertThreshold > thresholds.flagThreshold ? 'pointer' : 'not-allowed',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.9rem',
            opacity: hasChanges && isWeightsValid && thresholds.alertThreshold > thresholds.flagThreshold ? 1 : 0.5
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          onClick={handleResetWeights}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.textMid,
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.9rem'
          }}
        >
          Reset Weights
        </button>
        <button
          onClick={handleResetThresholds}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.textMid,
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.9rem'
          }}
        >
          Reset Thresholds
        </button>
      </div>
    </div>
  );
}
